import React, { useEffect, useState } from "react";
import { Card, Typography, Spin, Form, Input, Button, Switch, Divider, Row, Col } from "antd";
import { LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import UserServ from "../service/UserServ";
import GlobalHeader from "../util/GlobalHeader";
import { usePreference } from "../context/PreferenceContext";
import Notify from "../util/Notify";

const { Title, Text } = Typography;

const BuyerSecurity = () => {
    const { t } = usePreference();
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [form] = Form.useForm();

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await UserServ.getSecuritySettings();
            if (res.data) {
                setSettings(res.data);
            }
        } catch (error) {
            console.error("Failed to load security settings", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handlePasswordChange = async (values) => {
        try {
            setPasswordLoading(true);
            await UserServ.changePassword({
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
            });
            Notify.success("Success", "Password changed successfully!");
            form.resetFields();
        } catch (error) {
            Notify.error("Error", error.response?.data?.message || "Failed to change password. Please check your current password.");
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <div className="pcu-padding-x-20">
            <GlobalHeader title={t('security_settings') || "Security Settings"}>
                <div className="pcu-margin-top-20">
                    {loading ? <div className="pcu-text-center pcu-padding-50"><Spin /></div> : (
                        <Row gutter={[24, 24]}>
                            <Col xs={24} md={12}>
                                <Card title={<><LockOutlined className="pcu-margin-right-10" />Change Password</>} className="pcu-radius-12">
                                    <Form
                                        form={form}
                                        layout="vertical"
                                        onFinish={handlePasswordChange}
                                        autoComplete="off"
                                    >
                                        <Form.Item
                                            label="Current Password"
                                            name="currentPassword"
                                            rules={[{ required: true, message: 'Please input your current password!' }]}
                                        >
                                            <Input.Password placeholder="Enter current password" size="large" />
                                        </Form.Item>

                                        <Form.Item
                                            label="New Password"
                                            name="newPassword"
                                            rules={[
                                                { required: true, message: 'Please input your new password!' },
                                                { min: 6, message: 'Password must be at least 6 characters!' }
                                            ]}
                                        >
                                            <Input.Password placeholder="Enter new password" size="large" />
                                        </Form.Item>

                                        <Form.Item
                                            label="Confirm New Password"
                                            name="confirmPassword"
                                            dependencies={['newPassword']}
                                            rules={[
                                                { required: true, message: 'Please confirm your new password!' },
                                                ({ getFieldValue }) => ({
                                                    validator(_, value) {
                                                        if (!value || getFieldValue('newPassword') === value) {
                                                            return Promise.resolve();
                                                        }
                                                        return Promise.reject(new Error('The two passwords do not match!'));
                                                    },
                                                }),
                                            ]}
                                        >
                                            <Input.Password placeholder="Confirm new password" size="large" />
                                        </Form.Item>

                                        <Form.Item className="pcu-margin-bottom-0">
                                            <Button type="primary" htmlType="submit" loading={passwordLoading} size="large" block className="pcu-radius-8">
                                                Update Password
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </Card>
                            </Col>

                            <Col xs={24} md={12}>
                                <Card title={<><SafetyCertificateOutlined className="pcu-margin-right-10" />Two-Factor Authentication</>} className="pcu-radius-12">
                                    <div className="pcu-flex-between pcu-align-center">
                                        <div>
                                            <Title level={5} className="pcu-margin-bottom-5">Login Alerts</Title>
                                            <Text type="secondary">Get notified when someone logs into your account.</Text>
                                        </div>
                                        <Switch
                                            checked={settings.loginAlerts || true}
                                            onChange={(checked) => {
                                                setSettings(prev => ({ ...prev, loginAlerts: checked }));
                                                Notify.success("Success", "Login alerts updated!");
                                            }}
                                        />
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
            </GlobalHeader>
        </div>
    );
};

export default BuyerSecurity;
